"use client";

import { css } from "styled-system/css";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import type { PlanType } from "@prisma/client";
import { formatTrialDaysLeftLabel } from "@/lib/trial-days";
import { useConfirm } from "@/hooks/useConfirm";

type TrialPlan = "PRO" | "ENTERPRISE";

export function ProfessionalActions({
  professionalId,
  isActive,
  plan,
  trialDaysLeft = null,
  planExpiresAt = null,
}: {
  professionalId: string;
  isActive: boolean;
  plan: PlanType;
  trialDaysLeft?: number | null;
  planExpiresAt?: string | null;
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const [trialPlan, setTrialPlan] = useState<TrialPlan>("PRO");
  const [trialDays, setTrialDays] = useState("30");
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialMsg, setTrialMsg] = useState<string | null>(null);

  async function patch(body: object) {
    setLoading(true);
    await fetch(`/api/admin/professionals/${professionalId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    setLoading(false);
    window.location.reload();
  }

  async function remove() {
    if (
      !(await confirm({
        message: "¿Eliminar definitivamente esta cuenta y todos sus datos? Esta acción no se puede deshacer.",
        tone: "danger",
        confirmLabel: "Eliminar",
      }))
    )
      return;
    setLoading(true);
    const r = await fetch(`/api/admin/professionals/${professionalId}`, { method: "DELETE", credentials: "include" });
    setLoading(false);
    if (r.ok) navigate("/admin/professionals");
  }

  async function sendTrialInvite() {
    const n = Math.min(365, Math.max(1, parseInt(trialDays, 10) || 30));
    if (
      !(await confirm(
        `Se aplicará el plan ${trialPlan} con ${n} días de prueba, vencimiento actualizado y se enviará un correo. ¿Continuar?`,
      ))
    )
      return;
    setTrialBusy(true);
    setTrialMsg(null);
    const r = await fetch(`/api/admin/professionals/${professionalId}/trial-invite`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ plan: trialPlan, days: n }),
    });
    const j = (await r.json().catch(() => ({}))) as { error?: string; planExpiresAt?: string };
    setTrialBusy(false);
    if (!r.ok) {
      setTrialMsg(j.error ?? "No se pudo enviar.");
      return;
    }
    setTrialMsg("Listo: correo enviado y prueba aplicada.");
    window.location.reload();
  }

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "4", maxW: "sm" })}>
      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
        <Button
          type="button"
          variant="secondary"
          disabled={loading}
          onClick={() => void patch({ isActive: !isActive })}
        >
          {isActive ? "Desactivar cuenta" : "Activar cuenta"}
        </Button>
        <Button type="button" variant="danger" disabled={loading} onClick={() => void remove()}>
          Eliminar cuenta
        </Button>
      </div>
      <label className={css({ fontSize: "sm", display: "flex", flexDir: "column", gap: "2" })}>
        Plan
        <select
          className={css({
            px: "3",
            py: "2",
            rounded: "md",
            borderWidth: "1px",
            borderColor: "border.DEFAULT",
          })}
          value={plan}
          disabled={loading}
          onChange={(e) => void patch({ plan: e.target.value })}
        >
          <option value="FREE">FREE</option>
          <option value="PRO">PRO</option>
          <option value="ENTERPRISE">ENTERPRISE</option>
        </select>
      </label>
      <div
        className={css({
          borderTopWidth: "1px",
          borderColor: "border.DEFAULT",
          pt: "4",
          display: "flex",
          flexDir: "column",
          gap: "3",
        })}
      >
        <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "fg.DEFAULT" })}>Renovar / aplicar prueba gratis</p>
        {trialDaysLeft !== null ? (
          <p
            className={css({
              fontSize: "sm",
              fontWeight: "semibold",
              color: trialDaysLeft <= 0 ? "red.700" : trialDaysLeft <= 5 ? "#854d0e" : "green.800",
              px: "3",
              py: "2",
              rounded: "md",
              bg: trialDaysLeft <= 0 ? "#fee2e2" : trialDaysLeft <= 5 ? "#fef9c3" : "#ecfdf5",
            })}
          >
            Prueba actual: {formatTrialDaysLeftLabel(trialDaysLeft)}
            {planExpiresAt ? (
              <span className={css({ display: "block", fontWeight: "normal", fontSize: "xs", mt: "1", opacity: 0.9 })}>
                Vence el {new Date(planExpiresAt).toLocaleString("es-AR")}
              </span>
            ) : null}
          </p>
        ) : plan !== "FREE" ? (
          <p className={css({ fontSize: "xs", color: "fg.muted" })}>Sin período de prueba activo (sin fecha de vencimiento).</p>
        ) : null}
        <p className={css({ fontSize: "xs", color: "fg.muted", lineHeight: "1.5" })}>
          Aplica el plan elegido, actualiza la fecha de vencimiento y envía el mail de prueba. Quita el estado “pago pendiente” mientras
          dure la prueba.
        </p>
        <label className={css({ fontSize: "sm", display: "flex", flexDir: "column", gap: "2" })}>
          Plan de la prueba
          <select
            className={css({
              px: "3",
              py: "2",
              rounded: "md",
              borderWidth: "1px",
              borderColor: "border.DEFAULT",
            })}
            value={trialPlan}
            disabled={trialBusy}
            onChange={(e) => setTrialPlan(e.target.value as TrialPlan)}
          >
            <option value="PRO">PRO</option>
            <option value="ENTERPRISE">ENTERPRISE</option>
          </select>
        </label>
        <label className={css({ fontSize: "sm", display: "flex", flexDir: "column", gap: "2" })}>
          Días de prueba (1–365)
          <input
            type="number"
            min={1}
            max={365}
            value={trialDays}
            onChange={(e) => setTrialDays(e.target.value)}
            disabled={trialBusy}
            className={css({
              px: "3",
              py: "2",
              rounded: "md",
              borderWidth: "1px",
              borderColor: "border.DEFAULT",
            })}
          />
        </label>
        <Button type="button" variant="secondary" disabled={trialBusy || loading} onClick={() => void sendTrialInvite()}>
          {trialBusy ? "Enviando…" : "Aplicar prueba y enviar correo"}
        </Button>
        {trialMsg ? (
          <p className={css({ fontSize: "sm", color: trialMsg.startsWith("No") ? "red.700" : "green.700" })}>{trialMsg}</p>
        ) : null}
      </div>
    </div>
  );
}
